import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { useDeleteChartOfAccountsTemplateMutation } from "@api/coaTemplate.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ApplyCoaTemplateDrawer } from "./apply/ApplyCoaTemplateDrawer";
import { ChartOfAccountsTemplateAppliedEntitiesDrawer } from "./appliedEntities/ChartOfAccountsTemplateAppliedEntitiesDrawer";
import { ChartOfAccountsTemplateActivityDrawer } from "./activity/ChartOfAccountsTemplateActivityDrawer";
import { ChartOfAccountsTemplateModal } from "./ChartOfAccountsTemplateModal";

type SetTemplate = (template: ChartOfAccountsTemplate | null) => void;

export const ChartOfAccountsTemplateOverlays = ({
    applyingTemplate,
    setApplyingTemplate,
    editingTemplate,
    setEditingTemplate,
    duplicatingTemplate,
    setDuplicatingTemplate,
    deletingTemplate,
    setDeletingTemplate,
    viewingAppliedEntitiesTemplate,
    setViewingAppliedEntitiesTemplate,
    viewingActivityTemplate,
    setViewingActivityTemplate,
    onAddEntity,
    onEditTemplateFromApply,
}: {
    applyingTemplate: ChartOfAccountsTemplate | null;
    setApplyingTemplate: SetTemplate;
    editingTemplate: ChartOfAccountsTemplate | null;
    setEditingTemplate: SetTemplate;
    duplicatingTemplate: ChartOfAccountsTemplate | null;
    setDuplicatingTemplate: SetTemplate;
    deletingTemplate: ChartOfAccountsTemplate | null;
    setDeletingTemplate: SetTemplate;
    viewingAppliedEntitiesTemplate: ChartOfAccountsTemplate | null;
    setViewingAppliedEntitiesTemplate: SetTemplate;
    viewingActivityTemplate: ChartOfAccountsTemplate | null;
    setViewingActivityTemplate: SetTemplate;
    onAddEntity: () => void;
    onEditTemplateFromApply: () => void;
}) => {
    const { toast: successToast, error: errorToast } = useToaster();
    const deleteMutation = useDeleteChartOfAccountsTemplateMutation(
        deletingTemplate ?? undefined,
    );

    return (
        <>
            <ApplyCoaTemplateDrawer
                open={!!applyingTemplate}
                template={applyingTemplate ?? undefined}
                onClose={() => setApplyingTemplate(null)}
                onAddEntity={onAddEntity}
                onEditTemplate={onEditTemplateFromApply}
            />
            <ChartOfAccountsTemplateAppliedEntitiesDrawer
                open={!!viewingAppliedEntitiesTemplate}
                template={viewingAppliedEntitiesTemplate}
                onClose={() => setViewingAppliedEntitiesTemplate(null)}
            />
            <ChartOfAccountsTemplateActivityDrawer
                open={!!viewingActivityTemplate}
                template={viewingActivityTemplate}
                onClose={() => setViewingActivityTemplate(null)}
            />
            <ChartOfAccountsTemplateModal
                show={!!editingTemplate}
                template={editingTemplate ?? undefined}
                onHide={() => setEditingTemplate(null)}
            />
            <ChartOfAccountsTemplateModal
                show={!!duplicatingTemplate}
                duplicateSource={duplicatingTemplate ?? undefined}
                onHide={() => setDuplicatingTemplate(null)}
            />
            <ConfirmationModal
                show={!!deletingTemplate}
                title={`Delete "${deletingTemplate?.name}"?`}
                question="This template will be permanently removed."
                yes="Delete"
                no="Cancel"
                onReject={() => setDeletingTemplate(null)}
                onConfirm={async () => {
                    const templateName = deletingTemplate?.name ?? "";
                    try {
                        await deleteMutation.mutateAsync();
                        setDeletingTemplate(null);
                        successToast(`Deleted template "${templateName}"`);
                    } catch {
                        errorToast(
                            `Failed to delete template "${templateName}"`,
                        );
                    }
                }}
            />
        </>
    );
};
